import { PTEXPRemoteError } from '../errors/PTEXPRemoteError';

type LogMethod = 'error' | 'warn' | 'info' | 'debug';

interface LoggerInterface {
  error: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  info: (...args: any[]) => void;
  debug: (...args: any[]) => void;
}

/**
 * Unified NR-compatible error logger.
 *
 * Detects error type (DOM event, Axios, or generic) and logs a structured
 * JSON string with consistent fields (message, response, status, stack).
 * The message field concatenates label + error message for NR logs panel display.
 * Accepts any logger that matches the LoggerInterface (console or electron-log).
 *
 * @param error  - The caught error (Error, DOMException, ErrorEvent, Axios error, or unknown)
 * @param label  - A context label, e.g. "[ParentalConsentProvider] Failed to fetch consent age"
 * @param method - The log method to use (default: 'error')
 * @param logger - The logger instance to use (default: console)
 * @param extra  - Optional extra metadata to include in the payload
 */
const logErrorNR = (
  error: any,
  label: string,
  method: LogMethod = 'error',
  logger: LoggerInterface = console,
  extra: Record<string, any> = {}
): void => {
  const isAxiosError = !!error?.isAxiosError;
  const isDomEventError =
    (typeof Event !== 'undefined' && error instanceof Event) ||
    (typeof error === 'object' && error !== null && 'type' in error && 'error' in error);

  let response: string | null = null;
  let status: string | number | null = null;
  let message: string | null = null;
  let stack: string | null = null;

  if (isDomEventError) {
    const domError = error?.error ?? error;
    message = domError?.message ?? 'DOM error (no message)';
    stack =
      domError instanceof DOMException
        ? `DOMException: ${domError.name} (code: ${domError.code})`
        : domError?.stack ?? null;
    response = error?.type ?? null;
    status = domError instanceof DOMException ? domError.code : null;
  } else if (isAxiosError || error instanceof PTEXPRemoteError) {
    response = error?.response?.data ? JSON.stringify(error.response.data).slice(0, 1500) : null;
    status = error?.response?.status ?? null;
    message = error?.message ?? null;
    stack = error?.stack ?? null;
  } else {
    message = error?.message ?? (typeof error === 'string' ? error : 'Unknown error');
    stack = error?.stack ?? null;
    status = error?.code ?? null;
  }

  const payload = {
    message: message ? `${label} - ${message}` : label,
    response,
    status,
    stack,
    ...(error instanceof PTEXPRemoteError && error.errorId ? { errorId: error.errorId } : {}),
    ...extra
  };

  logger[method](JSON.stringify(payload));
};

export { logErrorNR };
